import * as React from 'react';
import { keyBy, isEqual } from 'lodash';
import { ConfigContext } from 'modules/context';

// components
import PlatformButton from 'components/platform-selection-button';
// constants
import { PLATFORM_TYPES, DATA_PLATFORMS_AMOUNT } from '@sitegen/common/constants/platforms';
import { PLATFORM_TO_EXTERNAL_LOGIN_OPTION } from 'modules/ubisoft-connect-service/constants';

// types
import { IProfilePlatform } from 'types/profiles';
import { IPlatformListContainerProps } from './platform-list.types';

// utils
import { isProfilePlatformLinkable, getProfilePlatformById } from '../../redemption.utils';
import { getAnalyticsDataOnPlatformClick } from './platform-list.utils';
// service
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

import './platform-list.styles.less';

interface IPlatformListContainerState {
    readonly profilePlatformsByPlatformId: {[key: string]: IProfilePlatform};
}

class PlatformList extends React.PureComponent<
IPlatformListContainerProps,
IPlatformListContainerState
> {
    public state = {
        profilePlatformsByPlatformId: keyBy(this.props.profilePlatforms, 'id')
    };

    public componentDidUpdate(prevProps: IPlatformListContainerProps) {
        if (!isEqual(prevProps.profilePlatforms, this.props.profilePlatforms)) {
            this.setState({
                profilePlatformsByPlatformId: keyBy(this.props.profilePlatforms, 'id')
            });
        }
    }

    public tryLinkToPlatform = (profilePlatform: IProfilePlatform): void => {
        const wasLinked: boolean = !!(profilePlatform && profilePlatform.profileId);

        /** Note: currently STADIA cannot be linked from Sitegen widget,
         *  Remove this check when Ubisoft Connect supports STADIA profile linking
         */
        const canBeLinked: boolean = (!!profilePlatform && profilePlatform.id !== PLATFORM_TYPES.STADIA) &&
            isProfilePlatformLinkable(profilePlatform);

        if (!wasLinked && canBeLinked) {
            const externalLoginOption = PLATFORM_TO_EXTERNAL_LOGIN_OPTION[profilePlatform.id];
            this.props.openPlatformLoginPopup(externalLoginOption);
        }
    };

    public handleSelect = (platformId: PLATFORM_TYPES | null) => {
        const {
            profilePlatforms,
            onSelectPlatform
        } = this.props;

        const { config: { widgetName } } = this.context;

        platformId && AnalyticsService.sendFlowEvent(
            getAnalyticsDataOnPlatformClick(widgetName, platformId)
        );

        const profilePlatform = getProfilePlatformById(platformId, profilePlatforms);
        if (profilePlatform) {
            this.tryLinkToPlatform(profilePlatform);
            onSelectPlatform(profilePlatform);
        }
    };

    public getPlatformButtons(platforms: PLATFORM_TYPES[]): JSX.Element[] {
        return platforms.map(platform => {
            const profilePlatform = this.state.profilePlatformsByPlatformId[platform];
            const userName = profilePlatform && profilePlatform.nameOnPlatform;

            return (
                <PlatformButton
                    key={platform}
                    platformId={platform}
                    selected={platform === this.props.activePlatformId}
                    onClick={this.handleSelect}
                    userName={userName}
                />
            );
        });
    }

    public render(): JSX.Element {
        /** Note: We shouldn`t display the platforms unknown to Connect */
        const platforms = this.props.platformsList.filter(
            platform => this.state.profilePlatformsByPlatformId[platform]
        );

        return <div
            {...{
                className: 'sitegen-redeem-platform-list',
                [DATA_PLATFORMS_AMOUNT]: platforms.length
            }}
        >
            {this.getPlatformButtons(platforms)}
        </div>;
    }
}

PlatformList.contextType = ConfigContext;

export default PlatformList;
